import { Fragment } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material'

const InstructionCard = ({ title, icon, sections = [] }) => {
  const validSections = (sections ?? []).filter((section) => {
    if (!section?.title) return false

    if (section.variant === 'table') {
      return (
        Array.isArray(section.columns) &&
        section.columns.length > 0 &&
        Array.isArray(section.rows) &&
        section.rows.length > 0
      )
    }

    if (section.variant === 'notes') {
      return (
        Array.isArray(section.groups) &&
        section.groups.length > 0 &&
        section.groups.some((g) => Array.isArray(g?.items) && g.items.length > 0)
      )
    }

    // Backwards-compatible fallback: bullet list sections
    return Array.isArray(section.items) && section.items.length > 0
  })

  return (
    <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <CardContent>
        {title && (
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {icon && (
              <Box component="span" sx={{ display: 'inline-flex', color: '#e90000' }}>
                {icon}
              </Box>
            )}
            {title}
          </Typography>
        )}

        {validSections.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {validSections.map((section, index) => (
              <Fragment key={section.title}>
                {index > 0 && <Divider />}

                {section.variant === 'table' ? (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#1a1a1a', display: 'block' }}>
                      {section.title}
                    </Typography>

                    <TableContainer
                      component={Paper}
                      sx={{
                        mt: 1,
                        overflowX: 'auto',
                        '& .MuiTableCell-root': {
                          borderColor: '#e0e0e0',
                          py: 1,
                        },
                      }}
                    >
                      <Table size="small" sx={{ minWidth: 320, '& th': { borderColor: '#e0e0e0' } }}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                            {section.columns.map((col) => (
                              <TableCell key={col} sx={{ fontWeight: 700 }}>
                                {col}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {section.rows.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                              {row.map((cell, cellIndex) => (
                                <TableCell
                                  key={cellIndex}
                                  sx={{
                                    color:
                                      cellIndex === 1
                                        ? String(cell).toLowerCase().startsWith('yes')
                                          ? '#e90000'
                                          : '#1a1a1a'
                                        : '#666',
                                    fontWeight: cellIndex === 1 ? 700 : 500,
                                  }}
                                >
                                  {cell}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ) : section.variant === 'notes' ? (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#1a1a1a', display: 'block' }}>
                      {section.title}
                    </Typography>

                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {(section.groups ?? []).map((group, groupIndex) => (
                        <Box
                          key={group.heading || groupIndex}
                          sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}
                        >
                          {group.heading ? (
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: '#1a1a1a',
                                display: 'block',
                              }}
                            >
                              {group.heading}
                            </Typography>
                          ) : null}

                          {(group.items ?? []).map((item, itemIndex) => (
                            <Box
                              // Using itemIndex to avoid key collisions when the same text repeats
                              key={`${itemIndex}-${item}`}
                              sx={{
                                bgcolor: '#f8f9fb',
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                px: 1.25,
                                py: 0.65,
                                width: 'fit-content',
                                maxWidth: '100%',
                              }}
                            >
                              <Typography variant="caption" sx={{ color: '#666' }}>
                                {item}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#1a1a1a', display: 'block' }}>
                      {section.title}
                    </Typography>

                    <Typography variant="caption" sx={{ color: '#666' }}>
                      {section.items.map((item, itemIndex) => (
                        <Fragment key={itemIndex}>
                          {itemIndex > 0 && <br />}
                          • {item}
                        </Fragment>
                      ))}
                    </Typography>
                  </Box>
                )}
              </Fragment>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default InstructionCard
